import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { connect, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import ExportDropdown from "../../../Components/Export"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { serializeData } from "../../../Utils/utils"
import UserCreators from "../../User/reducer"
import RequestsCreators, { selectRequestResult } from "../reducer"
import { getActionColumn } from "../RequestsEnrollments/components/Table/columns"
import { getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

const pageSize = 50

const { Text } = Typography

const pinnedColumns = ["#"]

const RequestsIntegrationsPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  exportFetching,
  exportResult,
  statistics,
  test_data,
  ...props
}) => {
  const navigate = useNavigate()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)
  const requestResult = useSelector(selectRequestResult)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const statisticsFolder = statistics?.["integrations_counter"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    document.title = "Requests Integrations - Chargeback"
  }, [])

  const prevTestDataRef = useRef(test_data)

  useEffect(() => {
    if (requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && { columns: ["sequence_number", "id", ...columns].join(",") })
    }
    props.getInfo({ ...data })
  }, [businessAccountId, ordering, pageNumber, filters, test_data, myColumnsLength, requestResult])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description: "Integrations requests will be downloaded shortly.",
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: count ? "max-content" : 230 * myColumns.filter(({ active }) => active).length,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_integrations_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={props.clearFilters}
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }),
    [myColumnsLength, count, pageNumber, statisticsFolder]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns()
    }
    return [
      allColumns["id"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {
    let business_account_id = data?.business_account?.id
    if (type === "business_account") {
      if (business_account_id) {
        props.selectBusinessAccountId(business_account_id)
        navigate(`/business_settings/enrollment`)
      }
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Confirm deleting",
        content: "Are you sure you want to delete this request? This action can NOT be undone.",

        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteRequest(data.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "add_comment") {
      props.selectRequestAddComment(data)
      navigate(`?add_comment=${data?.id}`)
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getExport({
        file_name: "Requests Integrations report",
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      })
    },
    [filters, test_data]
  )

  return (
    <div className="requests-page">
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          //filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"requests"}
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData />
          <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={true} />

          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={tableSettings.locale}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsIntegrations.visibleFiltersAdmin,

  defaultColumnsAdmin: state.requestsIntegrations.defaultColumnsAdmin,

  columns: state.requestsIntegrations.columns,
  columnsAdmin: state.requestsIntegrations.columnsAdmin,

  fetching: state.requestsIntegrations.fetching,
  result: state.requestsIntegrations.result,

  filters: state.requestsIntegrations.filters,
  filterOptions: state.requestsIntegrations.filterOptions,

  exportFetching: state.requestsIntegrations.exportFetching,
  exportResult: state.requestsIntegrations.exportResult,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getInfo: (params) => dispatch(Creators.getRequestsIntegrationsRequest(params)),
  deleteRequest: (id) => dispatch(Creators.deleteRequestsIntegrationRequest(id)),

  getExport: (businessId) => dispatch(Creators.getRequestsIntegrationsExportRequest(businessId)),
  clearExportResult: () => dispatch(Creators.requestsIntegrationsSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.requestsIntegrationsSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.requestsIntegrationsSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.requestsIntegrationsSetProp(visibleFiltersType, filters)),
  clearFilters: () => dispatch(Creators.clearRequestsIntegrationsFilters()),

  selectBusinessAccountId: (businessAccountId) =>
    dispatch(UserCreators.userSetProp("admin_selected_business_account_id", businessAccountId)),
  selectRequestAddComment: (request) =>
    dispatch(RequestsCreators.requestsSetProp("selectedRequest", request))
})

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(RequestsIntegrationsPage))
